import { useEffect, useState } from 'react';
import {
  RefreshCw,
  Save,
  MessageSquare,
  Phone,
  PhoneCall,
  DollarSign,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  Zap,
  History,
} from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';

interface Setting {
  key: string;
  value: string;
  value_type: string | null;
  description: string | null;
}

interface RateConfig {
  key: string;
  label: string;
  description: string;
  type: 'decimal' | 'integer' | 'toggle';
  prefix?: string;
  suffix?: string;
  toggleOptions?: { value: string; label: string }[];
}

// Define all billing rate settings with their display configs
const COMMUNICATION_RATES: RateConfig[] = [
  {
    key: 'sms_rate_per_segment',
    label: 'SMS Rate (per segment)',
    description: 'Customer charge per SMS segment',
    type: 'decimal',
    prefix: '$',
  },
  {
    key: 'mms_rate_per_message',
    label: 'MMS Rate (per message)',
    description: 'Customer charge per MMS message',
    type: 'decimal',
    prefix: '$',
  },
  {
    key: 'voice_inbound_rate_per_minute',
    label: 'Voice Inbound Rate (per minute)',
    description: 'Customer charge per minute for inbound calls',
    type: 'decimal',
    prefix: '$',
  },
  {
    key: 'voice_outbound_rate_per_minute',
    label: 'Voice Outbound Rate (per minute)',
    description: 'Customer charge per minute for outbound calls',
    type: 'decimal',
    prefix: '$',
  },
  {
    key: 'voice_minimum_seconds',
    label: 'Voice Minimum Billable',
    description: 'Minimum billable seconds for voice calls',
    type: 'integer',
    suffix: 'seconds',
  },
];

const PHONE_LINE_SETTINGS: RateConfig[] = [
  {
    key: 'phone_line_default_cost',
    label: 'Default Phone Line Cost',
    description: 'Default monthly cost per phone line if Twilio API unavailable',
    type: 'decimal',
    prefix: '$',
    suffix: '/month',
  },
  {
    key: 'phone_line_markup_multiplier',
    label: 'Phone Line Markup',
    description: 'Multiplier applied to phone line costs (e.g., 1.02 = 2% markup)',
    type: 'decimal',
    suffix: 'x',
  },
];

const LIST_CLEANER_PRICING: RateConfig[] = [
  {
    key: 'default_landline_cost',
    label: 'Landline Check Cost',
    description: 'Our cost per landline API check',
    type: 'decimal',
    prefix: '$',
  },
  {
    key: 'default_landline_price',
    label: 'Landline Check Price',
    description: 'Customer charge per landline check',
    type: 'decimal',
    prefix: '$',
  },
  {
    key: 'default_tcpa_cost',
    label: 'TCPA Check Cost',
    description: 'Our cost per TCPA API check',
    type: 'decimal',
    prefix: '$',
  },
  {
    key: 'default_tcpa_price',
    label: 'TCPA Check Price',
    description: 'Customer charge per TCPA check',
    type: 'decimal',
    prefix: '$',
  },
];

const PAYMENT_SETTINGS: RateConfig[] = [
  {
    key: 'payment_retry_delay_hours',
    label: 'Payment Retry Delay',
    description: 'Hours between payment retry attempts',
    type: 'integer',
    suffix: 'hours',
  },
  {
    key: 'payment_max_retries',
    label: 'Maximum Payment Retries',
    description: 'Maximum payment retry attempts before abandoning',
    type: 'integer',
    suffix: 'attempts',
  },
];

const ENVIRONMENT_SETTINGS: RateConfig[] = [
  {
    key: 'stripe_environment',
    label: 'Stripe Environment',
    description: 'Toggle between test and live Stripe mode',
    type: 'toggle',
    toggleOptions: [
      { value: 'test', label: 'Test Mode' },
      { value: 'live', label: 'Live Mode' },
    ],
  },
];

// All setting keys we care about
const ALL_SETTING_KEYS = [
  ...COMMUNICATION_RATES,
  ...PHONE_LINE_SETTINGS,
  ...LIST_CLEANER_PRICING,
  ...PAYMENT_SETTINGS,
  ...ENVIRONMENT_SETTINGS,
].map((r) => r.key);

export default function AdminBillingRates() {
  const [settings, setSettings] = useState<Map<string, Setting>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/settings`, {
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success) {
        // Build a map of settings we care about
        const settingsMap = new Map<string, Setting>();
        for (const setting of data.settings || []) {
          if (ALL_SETTING_KEYS.includes(setting.key)) {
            settingsMap.set(setting.key, setting);
          }
        }
        setSettings(settingsMap);
      } else {
        setError(data.error || 'Failed to fetch settings');
      }
    } catch (err) {
      console.error('[BillingRates] Error:', err);
      setError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  // Parse JSONB value to display value
  const parseValue = (setting: Setting | undefined): string => {
    if (!setting) return '';
    try {
      // JSONB values are stored with quotes for strings
      const parsed = JSON.parse(setting.value);
      return String(parsed);
    } catch {
      return setting.value;
    }
  };

  // Format value for storage
  // Note: Supabase handles JSONB serialization, so we just return the plain value
  const formatValueForStorage = (value: string, valueType: string | null): string => {
    if (valueType === 'boolean') {
      return value === 'true' ? 'true' : 'false';
    }
    // For string, decimal, integer - return plain value
    // Supabase client handles JSONB serialization automatically
    return value;
  };

  const handleEdit = (key: string) => {
    const setting = settings.get(key);
    setEditingKey(key);
    setEditValue(parseValue(setting));
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleSave = async (key: string, config: RateConfig) => {
    setSaving(true);
    setSaveSuccess(null);

    try {
      // Determine value type from config
      let valueType = 'string';
      if (config.type === 'decimal') valueType = 'decimal';
      else if (config.type === 'integer') valueType = 'integer';

      const formattedValue = formatValueForStorage(editValue, valueType);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: formattedValue }),
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setEditingKey(null);
        setEditValue('');
        setSaveSuccess(key);
        setTimeout(() => setSaveSuccess(null), 2000);
        fetchSettings();
      } else {
        alert(data.error || 'Failed to update setting');
      }
    } catch (err) {
      console.error('[BillingRates] Save error:', err);
      alert('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (key: string, _config: RateConfig, newValue: string) => {
    setSaving(true);
    setSaveSuccess(null);

    try {
      const formattedValue = formatValueForStorage(newValue, 'string');

      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: formattedValue }),
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setSaveSuccess(key);
        setTimeout(() => setSaveSuccess(null), 2000);
        fetchSettings();
      } else {
        alert(data.error || 'Failed to update setting');
      }
    } catch (err) {
      console.error('[BillingRates] Toggle error:', err);
      alert('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  if (loading && settings.size === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing Rates</h1>
          <p className="text-gray-500 mt-1">Configure pricing for communications and services</p>
        </div>
        <button
          onClick={fetchSettings}
          disabled={loading}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}

      {/* Communication Rates */}
      <RateSection
        title="Communication Rates"
        description="Pricing for SMS, MMS, and voice communications"
        icon={MessageSquare}
        iconBg="bg-blue-100"
        iconColor="text-blue-600"
        configs={COMMUNICATION_RATES}
        settings={settings}
        editingKey={editingKey}
        editValue={editValue}
        setEditValue={setEditValue}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onToggle={handleToggle}
        saving={saving}
        saveSuccess={saveSuccess}
        parseValue={parseValue}
      />

      {/* Phone Line Settings */}
      <RateSection
        title="Phone Line Settings"
        description="Default costs and markup for phone lines"
        icon={Phone}
        iconBg="bg-green-100"
        iconColor="text-green-600"
        configs={PHONE_LINE_SETTINGS}
        settings={settings}
        editingKey={editingKey}
        editValue={editValue}
        setEditValue={setEditValue}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onToggle={handleToggle}
        saving={saving}
        saveSuccess={saveSuccess}
        parseValue={parseValue}
      />

      {/* List Cleaner Pricing */}
      <RateSection
        title="List Cleaner Pricing"
        description="Cost and pricing for landline and TCPA checks"
        icon={PhoneCall}
        iconBg="bg-purple-100"
        iconColor="text-purple-600"
        configs={LIST_CLEANER_PRICING}
        settings={settings}
        editingKey={editingKey}
        editValue={editValue}
        setEditValue={setEditValue}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onToggle={handleToggle}
        saving={saving}
        saveSuccess={saveSuccess}
        parseValue={parseValue}
      />

      {/* Payment Settings */}
      <RateSection
        title="Payment Settings"
        description="Configure payment retry behavior"
        icon={CreditCard}
        iconBg="bg-orange-100"
        iconColor="text-orange-600"
        configs={PAYMENT_SETTINGS}
        settings={settings}
        editingKey={editingKey}
        editValue={editValue}
        setEditValue={setEditValue}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onToggle={handleToggle}
        saving={saving}
        saveSuccess={saveSuccess}
        parseValue={parseValue}
      />

      {/* Environment */}
      <RateSection
        title="Environment"
        description="Stripe payment environment configuration"
        icon={Zap}
        iconBg="bg-yellow-100"
        iconColor="text-yellow-600"
        configs={ENVIRONMENT_SETTINGS}
        settings={settings}
        editingKey={editingKey}
        editValue={editValue}
        setEditValue={setEditValue}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onToggle={handleToggle}
        saving={saving}
        saveSuccess={saveSuccess}
        parseValue={parseValue}
      />

      {/* Maintenance */}
      <PlanChargeBackfill />
    </div>
  );
}

// Plan Charge Back-fill maintenance tool
function PlanChargeBackfill() {
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [result, setResult] = useState<any | null>(null);
  const [mode, setMode] = useState<'preview' | 'applied' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const run = async (confirm: boolean) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/admin/billing/backfill-plan-charges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(confirm ? { confirm: true } : {}),
      });
      const text = await res.text();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let data: any;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || `HTTP ${res.status}`);
      }
      if (!data.success) {
        setErr(data.error || 'Back-fill failed');
        return;
      }
      setResult(data);
      setMode(confirm ? 'applied' : 'preview');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const s = result?.summary;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          <div className="bg-rose-100 p-2 rounded-lg">
            <History className="w-5 h-5 text-rose-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Plan Charge Back-fill</h2>
            <p className="text-sm text-gray-500">Rebuild missing plan payment records from Stripe invoices</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        <p className="text-sm text-gray-600">
          Scans every paid Stripe invoice and inserts any missing{' '}
          <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">plan_charge</code> records, plus adds
          plan metadata to existing ones. Run <strong>Preview</strong> first — it writes nothing. Safe to re-run.
        </p>

        <div className="flex gap-2">
          <button
            onClick={() => run(false)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Preview (Dry Run)
          </button>
          {mode === 'preview' && s && s.inserted + s.updated > 0 && (
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              Apply Back-fill
            </button>
          )}
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{err}</span>
          </div>
        )}

        {s && (
          <div
            className={`rounded-lg border p-4 ${
              mode === 'applied' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
            }`}
          >
            <div className="flex items-center gap-2 mb-3">
              {mode === 'applied' ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <History className="w-4 h-4 text-blue-600" />
              )}
              <span className="text-sm font-medium text-gray-900">
                {mode === 'applied' ? 'Back-fill applied' : 'Dry run preview — nothing written'}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <BackfillStat label="Invoices scanned" value={s.invoices_scanned} />
              <BackfillStat label="OAK plan invoices" value={s.oak_invoices} />
              <BackfillStat label={mode === 'applied' ? 'Rows inserted' : 'Rows to insert'} value={s.inserted} highlight />
              <BackfillStat label={mode === 'applied' ? 'Rows patched' : 'Rows to patch'} value={s.updated} highlight />
              <BackfillStat label="Skipped (non-OAK)" value={s.skipped_non_oak} />
              <BackfillStat label="Skipped (zero amount)" value={s.skipped_zero_amount} />
              <BackfillStat label="Skipped (no location)" value={s.skipped_no_location} />
              <BackfillStat label="Errors" value={s.errors?.length || 0} />
            </div>
            <div className="text-xs text-gray-500 mt-3">
              Batch: <span className="font-mono">{s.backfill_batch}</span>
            </div>
            {s.warnings?.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs font-medium text-amber-700 cursor-pointer">
                  {s.warnings.length} warning(s)
                </summary>
                <ul className="mt-1 text-xs text-amber-700 list-disc pl-5 space-y-0.5 max-h-40 overflow-auto">
                  {s.warnings.map((w: string, i: number) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </details>
            )}
            {s.errors?.length > 0 && (
              <details className="mt-2" open>
                <summary className="text-xs font-medium text-red-700 cursor-pointer">
                  {s.errors.length} error(s)
                </summary>
                <ul className="mt-1 text-xs text-red-700 list-disc pl-5 space-y-0.5 max-h-40 overflow-auto">
                  {s.errors.map((e: string, i: number) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        title="Apply Plan Charge Back-fill"
        message={
          s
            ? `This will insert ${s.inserted} new plan_charge row(s) and patch ${s.updated} existing row(s) with plan metadata. The operation is idempotent and safe to re-run. Continue?`
            : 'Continue?'
        }
        confirmLabel="Apply"
        onConfirm={() => {
          setConfirmOpen(false);
          run(true);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function BackfillStat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div>
      <div className={`text-lg font-bold ${highlight ? 'text-rose-700' : 'text-gray-900'}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

// Rate Section Component
function RateSection({
  title,
  description,
  icon: Icon,
  iconBg,
  iconColor,
  configs,
  settings,
  editingKey,
  editValue,
  setEditValue,
  onEdit,
  onSave,
  onCancel,
  onToggle,
  saving,
  saveSuccess,
  parseValue,
}: {
  title: string;
  description: string;
  icon: typeof DollarSign;
  iconBg: string;
  iconColor: string;
  configs: RateConfig[];
  settings: Map<string, Setting>;
  editingKey: string | null;
  editValue: string;
  setEditValue: (value: string) => void;
  onEdit: (key: string) => void;
  onSave: (key: string, config: RateConfig) => void;
  onCancel: () => void;
  onToggle: (key: string, config: RateConfig, value: string) => void;
  saving: boolean;
  saveSuccess: string | null;
  parseValue: (setting: Setting | undefined) => string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          <div className={`${iconBg} p-2 rounded-lg`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {configs.map((config) => {
          const setting = settings.get(config.key);
          const currentValue = parseValue(setting);
          const isEditing = editingKey === config.key;
          const justSaved = saveSuccess === config.key;

          return (
            <div key={config.key} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900">{config.label}</p>
                    {justSaved && (
                      <span className="inline-flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{config.description}</p>

                  {config.type === 'toggle' ? (
                    <>
                      <div className="mt-3 flex space-x-2">
                        {config.toggleOptions?.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => onToggle(config.key, config, option.value)}
                            disabled={saving}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              currentValue === option.value
                                ? option.value === 'live'
                                  ? 'bg-green-600 text-white'
                                  : 'bg-yellow-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {option.label}
                            {currentValue === option.value && option.value === 'live' && (
                              <AlertTriangle className="w-4 h-4 inline ml-2" />
                            )}
                          </button>
                        ))}
                      </div>
                      {config.key === 'stripe_environment' && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-800">
                            <strong>Important:</strong> When switching modes, you must also update the{' '}
                            <code className="bg-amber-100 px-1 py-0.5 rounded text-xs font-mono">STRIPE_SECRET_KEY</code>{' '}
                            Cloudflare Worker secret to match ({currentValue === 'live' ? 'rk_live_...' : 'sk_test_...'}).
                          </p>
                        </div>
                      )}
                    </>
                  ) : isEditing ? (
                    <div className="mt-2 flex items-center space-x-2">
                      <div className="relative flex-1 max-w-xs">
                        {config.prefix && (
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                            {config.prefix}
                          </span>
                        )}
                        <input
                          type={config.type === 'integer' ? 'number' : 'text'}
                          step={config.type === 'decimal' ? '0.0001' : '1'}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                            config.prefix ? 'pl-7' : ''
                          } ${config.suffix ? 'pr-16' : ''}`}
                          autoFocus
                        />
                        {config.suffix && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                            {config.suffix}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => onSave(config.key, config)}
                        disabled={saving}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={onCancel}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <button
                        onClick={() => onEdit(config.key)}
                        className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-mono transition-colors"
                      >
                        {config.prefix}
                        {currentValue || <span className="text-gray-400 italic">Not set</span>}
                        {config.suffix && currentValue && (
                          <span className="ml-1 text-gray-500">{config.suffix}</span>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
